import classNames from 'classnames';
import React from 'react';

import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {CourseBuyable} from '@udemy/shopping';

import {SHOPPING_CART_VARIANT_MOBILE} from '../../../../lib/experimentation-helper';
import styles from './shopping-item.module.less';

export interface MetaProps {
    buyable: CourseBuyable;
}

export const Meta = ({buyable}: MetaProps) => {
    const {ninterpolate} = useI18n();
    const metaItems: {key: string; value: string}[] = [];
    const {featureVariantAssignments} = useFeatures([SHOPPING_CART_VARIANT_MOBILE]);
    const hideMetaData =
        featureVariantAssignments.get(SHOPPING_CART_VARIANT_MOBILE)?.configuration?.enabled ??
        false;

    function getContentTotalLengthText(minutes: number) {
        const hours = Math.floor(Math.round(minutes) / 60);
        const normalizedMinutes = Math.round(minutes) % 60;
        const normalizedHours = hours + Math.floor((normalizedMinutes + 15) / 30) / 2;

        if (hours < 1) {
            return ninterpolate('%s total min', '%s total mins', normalizedMinutes);
        }
        return ninterpolate('%s total hour', '%s total hours', normalizedHours);
    }

    function getNumLecturesText(numLectures: number) {
        return ninterpolate('%s lecture', '%s lectures', numLectures);
    }

    if (buyable.estimated_content_length) {
        metaItems.push({
            key: 'estimated_content_length',
            value: getContentTotalLengthText(buyable.estimated_content_length),
        });
    }
    if (buyable.num_published_lectures) {
        metaItems.push({
            key: 'num_published_lectures',
            value: getNumLecturesText(buyable.num_published_lectures),
        });
    }
    if (buyable.instructional_level_simple) {
        metaItems.push({
            key: 'instructional_level_simple',
            value: buyable.instructional_level_simple,
        });
    }

    return (
        <div
            className={classNames('ud-text-xs', styles.meta, {
                [styles['hide-on-mobile']]: hideMetaData,
            })}
        >
            {metaItems.map(({key, value}, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <Icon glyph="bullet" label={false} size="xxsmall" color="subdued" />}
                    <span className={styles['meta-item']} data-testid={key}>
                        {value}
                    </span>
                </React.Fragment>
            ))}
        </div>
    );
};
